// Master Apps · Casca compartilhada das telas de acesso — as páginas que rodam
// FORA do app logado (sem sidebar): /aguardando, /primeiro-acesso e a tela de
// acesso restrito do RequireAdmin.
//
// Identidade visual única: tela cheia, brilho radial no acento Masterboi atrás
// do cartão, marca + alternador de tema no topo, cartão central e rodapé
// monoespaçado. O que varia (largura do cartão, respiro interno, alinhamento,
// conteúdo do cartão e o bloco abaixo dele) entra por props, de modo que as três
// telas não precisem repetir a mesma casca.
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { NocMark, ThemeToggle } from '@/components/primitives';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { fontSans, makePalette, numeroTabular, type Palette } from '@/lib/theme';
import { getClerk, useSessionStore } from '@/store/session';
import { useScreenProps } from '@/store/theme';

// Abertura de chamado na TI — destino dos botões "Falar com a TI".
export const SUPPORT_URL = 'https://masterboi.sensrit.com.br';

/** Botão de ação principal das telas de acesso: acento cheio, texto branco. */
export function primaryButtonStyle(accent: string): CSSProperties {
  return {
    padding: '11px 20px',
    borderRadius: 10,
    border: 'none',
    background: accent,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: fontSans,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    textDecoration: 'none',
  };
}

/**
 * Botão de ação secundária das telas de acesso: contorno neutro, sem
 * preenchimento. Em `<a>` acrescente `textDecoration: 'none'` — só o link
 * precisa desligar o sublinhado do navegador.
 */
export function secondaryButtonStyle(p: Palette): CSSProperties {
  return {
    padding: '11px 18px',
    borderRadius: 10,
    border: `1px solid ${p.borderStrong}`,
    background: 'transparent',
    color: p.text,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: fontSans,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };
}

/**
 * Logout das telas de acesso. Derruba a sessão local, encerra a sessão do Clerk
 * e vai para /login sem esperar o Clerk. A promessa do Clerk é tratada com
 * `.catch` em vez de descartada com `void`: rejeição em silêncio esconderia
 * "saí daqui e continuo logado no Clerk". `scope` só rotula o log.
 */
export function useAuthSignOut(scope: string): () => void {
  const navigate = useNavigate();
  const signOutLocal = useSessionStore((s) => s.signOut);
  return () => {
    signOutLocal();
    getClerk()
      ?.signOut()
      .catch((e: unknown) => {
        console.error(`[${scope}] falha ao encerrar a sessão no Clerk:`, e);
      });
    navigate('/login');
  };
}

/**
 * Corpo padrão do cartão: emblema circular no acento (opcional), título,
 * descrição e a fileira de ações. Cobre a tela de acesso restrito e as três
 * variantes de /aguardando (pendente, recusado e falha ao carregar o perfil —
 * essa última sem emblema e com o título um passo menor).
 */
export function AuthCardBody({
  badge,
  title,
  titleSize = 22,
  titleTracking = '-0.022em',
  description,
  actions,
}: Readonly<{
  badge?: ReactNode;
  title: ReactNode;
  titleSize?: number;
  titleTracking?: string;
  description: ReactNode;
  actions: ReactNode;
}>) {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  return (
    <>
      {badge && (
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            background: `${sp.accent}14`,
            color: sp.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
          }}
        >
          {badge}
        </div>
      )}
      <div
        style={{
          fontSize: titleSize,
          fontWeight: 600,
          letterSpacing: titleTracking,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13.5, color: p.muted, lineHeight: 1.6, marginBottom: 24 }}>
        {description}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {actions}
      </div>
    </>
  );
}

export function AuthShell({
  children,
  below,
  maxWidth = 440,
  cardPadding = '36px 34px 32px',
  cardPaddingCompact = '26px 20px 24px',
  centered = true,
}: Readonly<{
  /** Conteúdo do cartão central. */
  children: ReactNode;
  /** Bloco opcional logo abaixo do cartão, na mesma largura (conta conectada, sair). */
  below?: ReactNode;
  maxWidth?: number;
  cardPadding?: string;
  /**
   * Respiro do cartão até 1024px. Separado do `cardPadding` porque o padding
   * lateral do desktop (34px) come 68 dos 292px que sobram num celular de
   * 320px — e todas as telas desta casca querem o mesmo aperto.
   */
  cardPaddingCompact?: string;
  /** Texto centralizado no cartão (telas de recado); desligado nas telas com formulário. */
  centered?: boolean;
}>) {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  const vp = useViewport();

  return (
    <div
      style={{
        width: '100%',
        // `dvh` no celular: com `100vh` o teclado do sistema (que encolhe a
        // viewport) mantinha a casca na altura da tela CHEIA, empurrando o
        // rodapé e o campo para fora. `dvh` acompanha a viewport que sobrou.
        // `--app-vh` de `globals.css`: mesmo efeito com fallback para navegador
        // que não conhece `dvh` (lá a declaração inteira seria ignorada).
        minHeight: 'var(--app-vh)',
        display: 'flex',
        flexDirection: 'column',
        background: p.bg,
        color: p.text,
        fontFamily: fontSans,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* brilho radial discreto atrás do cartão */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // 560px de brilho vazavam 120px de cada lado num celular de 320px, e a
          // casca (overflow hidden) passava a rolar na horizontal.
          width: byViewport(vp, { mobile: 300, tablet: 460, desktop: 560 }),
          height: byViewport(vp, { mobile: 300, tablet: 460, desktop: 560 }),
          borderRadius: '50%',
          background: `radial-gradient(circle, ${sp.accent}14 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* marca no topo */}
      <div
        style={{
          padding: byViewport(vp, { mobile: '16px 16px', desktop: '22px 32px' }),
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <NocMark size={30} />
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
          Master<span style={{ opacity: 0.55 }}>·</span>Apps
        </div>
        <div style={{ flex: 1 }} />
        <ThemeToggle />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: byViewport(vp, { mobile: '0 14px 24px', desktop: '0 24px 48px' }),
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div style={{ width: '100%', maxWidth }}>
          <div
            style={{
              background: p.surface,
              border: `1px solid ${p.border}`,
              borderRadius: 14,
              padding: byViewport(vp, { mobile: cardPaddingCompact, desktop: cardPadding }),
              textAlign: centered ? 'center' : undefined,
              boxShadow: sp.dark ? '0 18px 48px rgba(0,0,0,0.45)' : '0 18px 48px rgba(0,0,0,0.08)',
            }}
          >
            {children}
          </div>

          {below}
        </div>
      </div>

      <div
        style={{
          ...numeroTabular,
          padding: byViewport(vp, { mobile: '12px 16px', desktop: '14px 32px' }),
          borderTop: `1px solid ${p.border}`,
          display: 'flex',
          // As duas linhas somam ~230px e se sobrepunham em 320px e 375px
          // (medido na mono, a sans é mais estreita): no celular empilham
          // centradas.
          flexDirection: byViewport(vp, { mobile: 'column', desktop: 'row' }),
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: byViewport(vp, { mobile: 3, desktop: 0 }),
          textAlign: 'center',
          fontSize: 11,
          color: p.muted,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <span>© 2026 Masterboi · Master Apps</span>
        <span>solucoes.masterbbr.com</span>
      </div>
    </div>
  );
}
